import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Divider, Grid, TextField, Typography } from '@mui/material'
import { MdEdit } from 'react-icons/md'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import InputMask from 'react-input-mask'
import DateTimeField from '../../../../../../../components/DateTimeField/DateTimeField'

const schema = z.object({
  dsEvolucao: z.string().min(1, 'Texto da evolução é obrigatório'),
  dtEvolucao: z.string().refine((val) => !dayjs(val).isAfter(dayjs()), {
    message: 'Não é permitido data/hora futura',
  }),
  vlAltura: z
    .string()
    .optional()
    .refine((v) => !v || (Number(v) >= 0 && Number(v) <= 3), {
      message: '0 a 3',
    }),
  vlPeso: z
    .string()
    .optional()
    .refine((v) => !v || (Number(v) >= 0 && Number(v) <= 500), {
      message: '0 a 500',
    }),
  nrTemperatura: z
    .string()
    .optional()
    .refine((v) => !v || (Number(v) >= 25 && Number(v) <= 45), {
      message: '25 a 45',
    }),
  nrFrequenciaRespiratoria: z
    .string()
    .optional()
    .refine((v) => !v || (Number(v) >= 0 && Number(v) <= 100), {
      message: '0 a 100',
    }),
  nrFrequenciaCardiaca: z
    .string()
    .optional()
    .refine((v) => !v || (Number(v) >= 0 && Number(v) <= 250), {
      message: '0 a 250',
    }),
  nrPaSistolica: z
    .string()
    .optional()
    .refine((v) => !v || (Number(v) >= 0 && Number(v) <= 300), {
      message: '0 a 300',
    }),
  nrPaDiastolica: z
    .string()
    .optional()
    .refine((v) => !v || (Number(v) >= 0 && Number(v) <= 250), {
      message: '0 a 250',
    }),
})

const FormEvolucao = ({ ativo, onSalvar, somenteVisualizacao = false }) => {
  const isVisualizacao = !!ativo || somenteVisualizacao

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      dsEvolucao: ativo?.[5] || '',
      dtEvolucao: ativo
        ? dayjs(ativo?.[4]).format('YYYY-MM-DDTHH:mm')
        : dayjs().format('YYYY-MM-DDTHH:mm'),
      vlAltura: ativo?.[6] ?? '',
      vlPeso: ativo?.[7] ?? '',
      nrTemperatura: ativo?.[8] ?? '',
      nrFrequenciaRespiratoria: ativo?.[9] ?? '',
      nrFrequenciaCardiaca: ativo?.[10] ?? '',
      nrPaSistolica: ativo?.[11] ?? '',
      nrPaDiastolica: ativo?.[12] ?? '',
    },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    reset({
      dsEvolucao: ativo?.[5] || '',
      dtEvolucao: ativo
        ? dayjs(ativo?.[4]).format('YYYY-MM-DDTHH:mm')
        : dayjs().format('YYYY-MM-DDTHH:mm'),
      vlAltura: ativo?.[6] ?? '',
      vlPeso: ativo?.[7] ?? '',
      nrTemperatura: ativo?.[8] ?? '',
      nrFrequenciaRespiratoria: ativo?.[9] ?? '',
      nrFrequenciaCardiaca: ativo?.[10] ?? '',
      nrPaSistolica: ativo?.[11] ?? '',
      nrPaDiastolica: ativo?.[12] ?? '',
    })
  }, [ativo, reset])

  const fieldProps = (name, label, rest = {}) => ({
    ...register(name),
    label,
    fullWidth: true,
    size: 'small',
    margin: 'dense',
    variant: 'outlined',
    value: watch(name),
    onChange: (e) => setValue(name, e.target.value),
    disabled: isVisualizacao,
    error: !!errors?.[name],
    helperText: errors?.[name]?.message || '',
    ...rest,
  })

  return (
    <form onSubmit={handleSubmit(onSalvar)} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <DateTimeField
            label="Data e Hora do Registro"
            value={watch('dtEvolucao')}
            onChange={(newValue) =>
              setValue(
                'dtEvolucao',
                newValue ? dayjs(newValue).format('YYYY-MM-DDTHH:mm') : '',
              )
            }
            disabled={isVisualizacao}
            maxDateTime={dayjs()}
            slotProps={{
              textField: {
                size: 'small',
                error: !!errors?.dtEvolucao,
                helperText: errors?.dtEvolucao?.message || '',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Divider />
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Sinais Vitais
          </Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField {...fieldProps('vlPeso', 'Peso', { type: 'number' })} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <InputMask
            mask="9.99"
            disabled={isVisualizacao}
            value={watch('vlAltura')}
            onChange={(e) => setValue('vlAltura', e.target.value)}
          >
            {(inputProps) => (
              <TextField
                {...inputProps}
                disabled={isVisualizacao}
                error={!!errors?.vlAltura}
                helperText={errors?.vlAltura?.message || ''}
                label="Altura"
                fullWidth
                size="small"
                margin="dense"
                variant="outlined"
              />
            )}
          </InputMask>
        </Grid>
        <Grid item xs={6} sm={3}>
          <InputMask
            mask="99.9"
            disabled={isVisualizacao}
            value={watch('nrTemperatura')}
            onChange={(e) =>
              setValue('nrTemperatura', e.target.value.replace(',', '.'))
            }
          >
            {(inputProps) => (
              <TextField
                {...inputProps}
                disabled={isVisualizacao}
                error={!!errors?.nrTemperatura}
                helperText={errors?.nrTemperatura?.message || ''}
                label="Temperatura"
                fullWidth
                size="small"
                margin="dense"
                variant="outlined"
              />
            )}
          </InputMask>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            {...fieldProps('nrFrequenciaRespiratoria', 'Freq. respiratória', {
              type: 'number',
            })}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            {...fieldProps('nrFrequenciaCardiaca', 'Freq. cardíaca', {
              type: 'number',
            })}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            {...fieldProps('nrPaSistolica', 'PA sistólica', {
              type: 'number',
            })}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            {...fieldProps('nrPaDiastolica', 'PA Diastólica', {
              type: 'number',
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...register('dsEvolucao')}
            label="Evolução de Enfermagem"
            multiline
            rows={6}
            fullWidth
            size="small"
            margin="dense"
            value={watch('dsEvolucao')}
            onChange={(e) => setValue('dsEvolucao', e.target.value)}
            disabled={isVisualizacao}
            error={!!errors?.dsEvolucao}
            helperText={errors?.dsEvolucao?.message || ''}
          />
        </Grid>
        {ativo ? (
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              Registrado por: {ativo?.[1]?.[1] || '---'} em{' '}
              {ativo?.[3] ? dayjs(ativo[3]).format('DD/MM/YYYY HH:mm') : '---'}
            </Typography>
          </Grid>
        ) : somenteVisualizacao ? (
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              Nenhuma evolução de enfermagem registrada.
            </Typography>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              startIcon={<MdEdit />}
            >
              Assinar
            </Button>
          </Grid>
        )}
      </Grid>
    </form>
  )
}

export default FormEvolucao
